import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { dashboardService } from '../api/dashboard.service';
import { DASHBOARD_KEYS } from './queryKeys';
import type {
  CreateWidgetPayload,
  UpdateWidgetPayload,
  ReorderWidgetsPayload,
  WidgetScope,
  WidgetDef,
  WidgetLayoutUpdate,
} from '../types';

interface WidgetLayoutMutation {
  widgetId: string;
  payload: UpdateWidgetPayload;
}

/**
 * Builds update payloads for layout changes, resending each widget's existing
 * title and config (updateWidget replaces config, so it must be carried over).
 */
export function toLayoutMutations(
  updates: WidgetLayoutUpdate[],
  widgets: WidgetDef[],
): WidgetLayoutMutation[] {
  return updates.flatMap((u) => {
    const widget = widgets.find((w) => w.id === u.id);
    if (!widget) return [];
    return [
      {
        widgetId: u.id,
        payload: {
          title: widget.title,
          desktop: u.desktop,
          tablet: u.tablet,
          mobile: u.mobile,
          ...widget.config,
        },
      },
    ];
  });
}

function useScopeId(scope: WidgetScope): string {
  if (scope.type === 'project') return scope.projectId ?? '';
  if (scope.type === 'board') return scope.boardId ?? '';
  return scope.tenantId;
}

export function useCreateWidget(scope: WidgetScope) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const scopeId = useScopeId(scope);

  return useMutation({
    mutationFn: (payload: CreateWidgetPayload) =>
      dashboardService.createWidget(scope.type, scopeId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: DASHBOARD_KEYS.shell(orgId, scope.type, scopeId),
      });
    },
  });
}

export function useUpdateWidget(scope: WidgetScope, widgetId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const scopeId = useScopeId(scope);

  return useMutation({
    mutationFn: (payload: UpdateWidgetPayload) =>
      dashboardService.updateWidget(scope.type, scopeId, widgetId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: DASHBOARD_KEYS.shell(orgId, scope.type, scopeId),
      });
      void queryClient.invalidateQueries({
        queryKey: DASHBOARD_KEYS.widgetData(orgId, scope.type, scopeId, widgetId),
      });
    },
  });
}

export function useDeleteWidget(scope: WidgetScope) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const scopeId = useScopeId(scope);

  return useMutation({
    mutationFn: (widgetId: string) => dashboardService.deleteWidget(scope.type, scopeId, widgetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: DASHBOARD_KEYS.shell(orgId, scope.type, scopeId),
      });
    },
  });
}

/**
 * Persists free-form grid layout changes (drag/resize) by updating each changed
 * widget. The backend has no bulk layout endpoint and `reorder` only sets the
 * order index, so positions/sizes must go through `updateWidget` per widget.
 * The full config is resent (updateWidget replaces it) to avoid wiping it.
 */
export function useUpdateWidgetLayouts(scope: WidgetScope) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const scopeId = useScopeId(scope);

  return useMutation({
    mutationFn: (updates: WidgetLayoutMutation[]) =>
      Promise.all(
        updates.map((u) =>
          dashboardService.updateWidget(scope.type, scopeId, u.widgetId, u.payload),
        ),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: DASHBOARD_KEYS.shell(orgId, scope.type, scopeId),
      });
    },
  });
}

export function useReorderWidgets(scope: WidgetScope) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const scopeId = useScopeId(scope);

  return useMutation({
    mutationFn: (payload: ReorderWidgetsPayload) =>
      dashboardService.reorderWidgets(scope.type, scopeId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: DASHBOARD_KEYS.shell(orgId, scope.type, scopeId),
      });
    },
  });
}
